import {
  attachmentTypes,
  surveyForm,
  chatEvents,
  cookieConstant,
  constantStrings,
  notificationConstants,
  status,
  enConstantStrings,
  eventType,
  chatWidgetScreenSizes,
  apiErrors,
  gradient
} from './constants'
import customAttributes from '@/services/constants/customAttributes'
import { getCookie, removeCookies, setCookie, removeCookie } from './cookie'
import store from '../store'
import { v4 as uuidV4 } from 'uuid'
import mqtt from './aws-iot/mqtt'
import Storage from '@/services/storageLib'
import { GlobalVars } from '@/services/globals'
import notification from '@/services/notification'
import AppError from './errors/app-errors'
import AppWarn from './errors/app-warns'
import session from '@/services/session'
import I18n from './localization/i18n-setup'
import validators from '@/services/validators'
import AppLog from '@/services/errors/app-logs'
import { getBotDetails, getLastRequestId } from '@/services/bots'
import { fetchAgentDeskConfig } from '@/services/liveAgent/utils/utils'
import agentDeskProperties from '@/services/liveAgent/constants/agentDeskProperties'
import OptionItemJsonConfig from '@/services/optionItemJsonConfig'
import WindowHelper from './windowHelper'
import calloutBubbleMessanger from './calloutBubbleMessanger'
import customFont from './customFont'
import fontSizes from './constants/fontSizes'
import { AUTH_CONSTANTS } from '../constants/authentication'
import { flattenObject } from './utilities/objectUtils.js'

export function getGradient(colors, direction) {
  if (!colors?.length) return
  if (colors.length === 1 && validateHexCode(colors[0])) {
    const secondColor = getShade(
      colors[0],
      gradient.gradientDirectionConfig.shadeValue
    )
    return `linear-gradient(114deg, ${colors[0]} 20%, ${secondColor} 100%)`
  }
  return colors.length > 1
    ? `linear-gradient(${gradient.gradientDirectionConfig.linearGradientDirection[direction]}, ${colors.join(', ')})`
    : ''
}

export function setBrowserAndDeviceDetailsInStore() {
  const userAgent = navigator.userAgent
  const browserMatchers = [
    { name: 'Edge', regex: /Edg\/([0-9\.]+)/ },
    { name: 'Firefox', regex: /Firefox\/([0-9\.]+)/ },
    { name: 'Opera', regex: /(OPR|Opera)\/([0-9\.]+)/ },
    { name: 'Chrome', regex: /Chrome\/([0-9\.]+)/ },
    { name: 'Safari', regex: /Version\/([0-9\.]+).*Safari/ },
    { name: 'Internet Explorer', regex: /(MSIE |rv:)([0-9\.]+)/ }
  ]
  let browserName = 'Unknown'
  let browserVersion = 'Unknown'
  for (const { name, regex } of browserMatchers) {
    const match = userAgent.match(regex)
    if (match) {
      browserName = name
      browserVersion = match[1]
      break
    }
  }
  const isMobile = /Mobi|Android/i.test(userAgent)
  const isTablet = /Tablet|iPad/i.test(userAgent)
  const deviceType = isMobile ? 'Mobile' : isTablet ? 'Tablet' : 'Desktop'
  let os = 'Unknown'
  const osMatchers = [
    { name: 'Windows', regex: /Win/ },
    { name: 'MacOS', regex: /Mac/ },
    { name: 'Linux', regex: /Linux/ },
    { name: 'Android', regex: /Android/ },
    { name: 'iOS', regex: /like Mac/ }
  ]
  for (const { name, regex } of osMatchers) {
    if (regex.test(userAgent)) {
      os = name
      break
    }
  }
  store.dispatch('updateUserDeviceDetails', {
    browserName,
    browserVersion,
    os,
    deviceType
  })
}

export function isEndChatSuveyValid() {
  const endChatSurveyRule = store.state.surveyRule.endChatRule.rule
  const isControlTypeBot = isTypeBOT(store.state.controlType)
  return (
    Object.keys(endChatSurveyRule).length &&
    isControlTypeBot &&
    !isScheduleSurveyPresentInCW()
  )
}

export function isEndChatGreetingValid(hideEndChatGreeting) {
  return (
    !hideEndChatGreeting && store.state.widgetConfig?.endChat?.endChatGreeting
  )
}

export function handleEndChatSurveyError() {
  if (isEndChatSurveyPresentInCW()) return
  if (store.state.clearConversationState.status === 'pending') {
    store.state.isTriggerMinimizeValid && store.dispatch('minimizeCW')
    store.dispatch('updateTriggerMinimizeValidState', false)
    store.dispatch('updateClearConversationToPending', '')
    store.dispatch(
      'handleClearConversationStatus',
      store.state.widgetConfig?.endChatClearConversation
    )
  }
}

export function isEndChatSurveyPresentInCW() {
  let surveyContainer = document.getElementById(
    `survey-form-container-${surveyForm.WIDGET_EVENT_CHAT_END}`
  )
  if (surveyContainer) return true
  return false
}

export function isScheduleSurveyPresentInCW() {
  let surveyContainer =
    document.getElementById(
      `survey-form-container-${surveyForm.WIDGET_EVENT_IDLE_USER}`
    ) ||
    document.getElementById(
      `survey-form-container-${surveyForm.WIDGET_EVENT_CHAT_END}`
    )
  if (surveyContainer) return true
  return false
}

export function emitCustomFieldsToParent(customFields) {
  const result = []
  customFields?.forEach((customField) => {
    if (customField && customField?.name) {
      const customObject = {}
      customObject[customField.name] =
        customField?.values?.length > 1
          ? customField.values
          : customField.values?.[0]
      result.push(customObject)
    }
  })
  emitEventToParentWindow(eventType.TRIGGER_PENDING_EVENTS_ON_HUMAN_HANDOFF, {
    customTags: result
  })
}

export function emitOauthEvents(oauthEventType) {
  emitEventToParentWindow(eventType.TRIGGER_PENDING_EVENTS_ON_OAUTH, {
    oauthType: oauthEventType
  })
}

export function handleIfConversationIdNull(message, doEndChat = false) {
  const conversationId = getCookie(cookieConstant.SESSION.CONVERSATION_ID)
  message += ` conversationId value in session: ${session.conversationId} and conversationId value in cookie: ${conversationId}`
  if (!conversationId || conversationId === 'null') {
    new AppError({ name: cookieConstant.SESSION.CONVERSATION_ID, message })
    if (doEndChat) {
      store.dispatch('endChat', {
        message: constantStrings.CHAT_ENDED,
        showSessionRestartGreeting: false,
        hideEndChatGreeting: true,
        clearConversation: false,
        hidePill: true
      })
    }
  }
}

export function updateUnreadIndicator(isEnabled = false) {
  store.state.widgetStatus.status === status.CLOSE &&
    store.dispatch('updateUnreadMessagesState', { enabled: isEnabled })
}

export function isValidURL(str) {
  var pattern = new RegExp(
    '(https://www.|http://www.|https://|http://)' +
      '?[a-zA-Z]{2,}(.[a-zA-Z]{2,})(.[a-zA-Z]{2,})?/[a-zA-Z0-9]{2,}|(' +
      '(https://www.|http://www.|https://|http://)?' +
      '[a-zA-Z]{2,}(.[a-zA-Z]{2,})(.[a-zA-Z]{2,})?)|' +
      '(https://www.|http://www.|https://|http://)?' +
      '[a-zA-Z0-9]{2,}.[a-zA-Z0-9]{2,}.[a-zA-Z0-9]{2,}(.[a-zA-Z0-9]{2,})?'
  )
  return !!pattern.test(str)
}

export function showMessageSeparatorPill({
  component,
  message,
  isEndChatSeparator,
  timestamp
}) {
  store.dispatch('showMessageSeparator', {
    isMessageSeparator: true,
    content: {
      component: component || 'EndChatSeparator',
      message,
      isEndChatSeparator // flag used for extracting current session chats from state
    },
    timestamp: timestamp || Date.now()
  })
}

export function clearTimersUponEndChat() {
  clearTimerForEndChat()
  clearIdleTimerForSurveyRule()
}

export function clearTimerForAgentDeskIdleTimeout() {
  const timerId = store.state.agentDesk.idleTimeoutTimerId
  if (timerId) clearTimeout(timerId)
}
export function clearTimerForEndChat() {
  const timerId = store.state.showEndChatFlow.timerId
  if (timerId) clearTimeout(timerId) // clear end chat timer if any
}

export function clearIdleTimerForSurveyRule() {
  const timerId = store.state.surveyRule.idleTimeoutRule.timerId
  if (timerId) clearTimeout(timerId)
}

export function clearTimerForInputFieldTemporaryDisable() {
  const timerId = store.state.inputFieldTempDisableStatusTimer
  if (timerId) {
    clearTimeout(timerId)
    store.dispatch('updateInputFieldTempDisableStatusTimer', '')
  }
}

export function clearTimerForInputFieldOnSendDisableTimer() {
  const timerId = store.state.inputFieldOnSendDisableTimer
  if (timerId) {
    clearTimeout(timerId)
    store.dispatch('updateInputFieldOnSendDisableTimer', '')
  }
}

export async function loadBotDetails() {
  if (!store.state.botDetails.timezone) {
    const botDetails = await getBotDetails()
    store.dispatch('updateBotDetails', botDetails?.data)
  }
}

export function getTimestampByTimezone(timeZone, timestamp = Date.now()) {
  return new Date(timestamp).toLocaleString('en-US', { timeZone })
}

export function getDomainOutOfURL(url) {
  try {
    url = new URL(url)
    let domain = url.hostname.replace('www.', '')
    domain = domain.substring(0, domain.lastIndexOf('.'))
    const pageName = url?.toString()?.split('/')?.pop()
    return `${domain}${pageName?.length ? '-' + pageName : ''}`
  } catch (error) {
    console.warn('Invalid URL in getDomainOutOfURL:', url, error)
    return ''
  }
}

export function getSourcesAsButtonsFromText(messages) {
  const multiSourcesMessage = messages?.find(({ messageValue }) => {
    const attachmentResponseType = messageValue?.attachmentResponseType
    if (
      attachmentResponseType &&
      attachmentResponseType === attachmentTypes.ANSWER_NET_MULTI_SOURCE
    ) {
      return messageValue
    }
  })?.messageValue

  const multiSources = multiSourcesMessage?.multipleSourceDetails
  const multiSourceText = multiSourcesMessage?.text
  if (!multiSources) return

  const sources = []

  multiSources?.forEach((buttonTextObj) => {
    const buttonText = buttonTextObj.text
    try {
      const isURL = isValidURL(buttonText)
      const source = {
        indexNumber: buttonTextObj?.index,
        text: isURL ? getDomainOutOfURL(buttonText) : buttonText,
        logo:
          store.state?.widgetConfig?.logoImage ||
          constantStrings.DEFAULT_LOGO_IMAGE ||
          'GlobeIcon',
        image: `${isURL ? 'Globe' : 'Description'}Icon`,
        ...(isURL && { url: buttonText })
      }
      sources.push(source)
    } catch (error) {
      console.error('Invalid Source Url', error)
      new AppError({
        name: 'Invalid Source Url',
        message: error.message,
        error
      })
    }
  })
  return { sources, multiSourceText }
}

export function hideMessage(message, type, isLastBotResponse) {
  const isTypeNbaResponse =
    message?.attachment?.attachmentResponseType === attachmentTypes.NBA_RESPONSE
  const isAttachmentTypeMultiSource =
    message?.attachment?.attachmentResponseType ===
    attachmentTypes.ANSWER_NET_MULTI_SOURCE
  if (isTypeNbaResponse) {
    type = attachmentTypes.NBA_RESPONSE
  }
  if (
    isAttachmentTypeMultiSource &&
    (!store?.getters?.isDynamicSourcesDisabled ||
      store.getters.isKnowledgeAiEnabled)
  ) {
    type = attachmentTypes.ANSWER_NET_MULTI_SOURCE
  }

  switch (type) {
    case 'HumanHandoffForm':
      return (
        message.attachment?.values['status'][0] === status.PENDING &&
        !isLastBotResponse
      )
    case attachmentTypes.NBA_RESPONSE:
    case 'FillerText':
      return true
    case 'TextResponse':
      return !validators.isEmpty(message.attachment.text)
    case attachmentTypes.ANSWER_NET_MULTI_SOURCE:
      return true
    case attachmentTypes.EMAIL:
      return !store.getters.isResponseAnalyzerEnabled
    default:
      return false
  }
}

export function isAgentDeskEvent(data) {
  return [
    chatEvents.AGENT_EVENT,
    chatEvents.QUEUE_POSITION,
    chatEvents.GENERIC
  ].includes(data?.eventData?.eventType)
}

export function idleTimeSurveyRequestFormatter(idleTime) {
  const data = {
    eventData: {
      eventType: chatEvents.WIDGET_EVENT,
      subType: constantStrings.IDLE_USER,
      eventInfo: {
        idleTime
      }
    },
    eventName: constantStrings.INFO
  }
  return data
}

export function requestFormatter(rootState, data) {
  const conversationId =
    data.conversationId || session.getConversationId(rootState.widgetConfig)
  const userMessage = data.userMessage
  const { user } = rootState

  const requestBody = {
    conversationId,
    ownerType: rootState.controlType,
    messagePayload: {
      text: userMessage?.payload,
      label: userMessage?.label,
      messageId: uuidV4(),
      timestamp: userMessage?.timestamp || Date.now(),
      hideMessage: userMessage?.hideMessage
    },
    attachmentList: data.attachments,
    userDetails: {
      userId: user.id,
      emailId: user.email,
      userName: user.name,
      phone: user.phone
    }
  }
  return requestBody
}

export const checkForGreetingsAndResetCookie = (data) => {
  if (data?.userMessage?.label === constantStrings.PROACTIVE_GREETING_LABEL) {
    setCookie(cookieConstant.GREETING.PROACTIVE_GREETING_COOKIE_LABEL, false, {
      SameSite: 'None',
      Secure: 'true',
      'max-age': getIdleTimeout() / 1000
    })
    return true
  }
  if (data?.userMessage?.label === constantStrings.CALLOUT_BUBBLE_TEXT) {
    setCookie(
      cookieConstant.GREETING.CALLOUT_BUBBLE_GREETING_COOKIE_LABEL,
      false,
      { SameSite: 'None', Secure: 'true', 'max-age': getIdleTimeout() / 1000 }
    )
    return true
  }
  return false
}

const createTextAttribute = (name, value) => ({
  type: 'TEXT',
  name,
  scope: 'LIFE_TIME',
  value
})

export const generateAttributes = (message) => {
  const attributes = [
    customAttributes.getWidgetId(),
    customAttributes.getUserDateAndTimeAttribute(),
    customAttributes.getUserTimezoneAttribute(),
    ...customAttributes.getWidgetVisitorInfo(),
    ...customAttributes.getDefaultAttributes(),
    ...customAttributes.getCustomVisitorInfo()
  ]

  if (getCookie(cookieConstant.AGENT_DESK.DEPT_HANDOFF_CODE_SENT)) {
    attributes.push(customAttributes.getDeptHandoffCode())
    removeCookie(cookieConstant.AGENT_DESK.DEPT_HANDOFF_CODE_SENT)
  }

  if (message?.customAttributes) {
    attributes.push(message.customAttributes)
  }

  if (message?.generatedBy) {
    attributes.push(createTextAttribute('generated_by', message.generatedBy))
  }

  const isAuthValue = localStorage.getItem(AUTH_CONSTANTS.ISAUTH)
  const validAuthStates = [String(true), AUTH_CONSTANTS.FAIL]
  if (validAuthStates.includes(isAuthValue)) {
    attributes.push(createTextAttribute(AUTH_CONSTANTS.ISAUTH, isAuthValue))
  }

  try {
    const rawData = localStorage.getItem(AUTH_CONSTANTS.USER_DETAILS) || '{}'
    const userDetails = JSON.parse(rawData)

    const flattened = flattenObject(userDetails, 'user')

    Object.entries(flattened).forEach(([key, value]) => {
      attributes.push(createTextAttribute(key, value))
    })
  } catch (e) {
    console.warn('Failed to parse user-details from localStorage', e)
  }
  return attributes
}

export const checkRenewTokenRequired = ({ isAuthenticated }) => {
  const { messageAuth, statusAuth } = isAuthenticated
  if (
    isUserLoggedIn() &&
    messageAuth === apiErrors.ERROR_MESSAGE.JWT_TOKEN_INVALID &&
    statusAuth === apiErrors.STATUS_CODE.UNAUTHORIZED
  ) {
    emitEventToParentWindow(eventType.INVALID_JWT_TOKEN_FALLBACK)
    store.dispatch('logoutWidget')
  }
}

export const getIdleTimeout = () => {
  if (isTypeBOT(store.state.controlType)) {
    return store.getters.getBotIdleTimeout
  }
  return Math.max(
    store.getters.getBotIdleTimeout,
    store.getters.getAgentDeskIdleTimeout
  )
}

export const getEndChatGreetingDelay = () => {
  return (
    parseInt(store.state.widgetConfig?.endChat?.endChatGreetingDelay) ||
    constantStrings.END_CHAT_GREETING_DELAY
  )
}

export const getSupportIconWidthAndHeight = () => {
  return store.state.widgetConfig?.supportIconConfig?.isSupportTextEnabled
    ? {
        ...chatWidgetScreenSizes.SHOW_SUPPORT_ICON_TEXT,
        width:
          document.getElementById('support-icon-container')?.clientWidth ||
          '290px'
      }
    : chatWidgetScreenSizes.SHOW_SUPPORT_ICON_ONLY
}

export const debounced = (func, timeout, immediate) => {
  let timer
  return (...args) => {
    const callNow = immediate && !timer
    clearTimeout(timer)
    timer = setTimeout(() => {
      timer = null
      !immediate && func.apply(this, args)
    }, timeout)
    if (callNow) {
      func.apply(this, args)
    }
  }
}

export const receiveHistoryChats = debounced(
  () => {
    store.dispatch('receiveHistoryOfMessages')
  },
  2000,
  true
)

export const isTypeBOT = (type = store.state.controlType) =>
  type === constantStrings.BOT

export const isMessageTypeInfo = (type) => type === constantStrings.INFO

export function getUserTimezone() {
  if (Intl) {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  }
  return ''
}

export function getUserDateAndTime() {
  const now = new Date()
  const day = new Intl.DateTimeFormat('en-GB', { weekday: 'long' }).format(now)
  const date = now.toLocaleDateString('en-GB')
  const time = now.toLocaleTimeString('en-GB', { hour12: false })
  return `${day} - ${date} - ${time}`
}

export function formatTime(timestamp) {
  return new Date(timestamp).toLocaleString('en-US', {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true
  })
}

export function formatDayTime(timestamp) {
  return new Date(timestamp).toLocaleString('en-US', {
    weekday: 'long', // "Saturday"
    day: '2-digit', // "01"
    month: 'long', // "June"
    year: 'numeric' // "2020"
  })
}

export function extractQuickReplyOptions(messages) {
  if (messages && messages.length > 0) {
    for (let i = messages.length - 1; i >= 0; i--) {
      const options = messages[i]?.messageValue?.quickReply?.options
      if (options && options.length > 0) {
        return options
      }
    }
  }
  return []
}

export function extractNBAOptions(messages) {
  let nbaObjects
  const actionableNBAOptions = []
  const nonActionableNBAOptions = []
  for (let i = messages?.length - 1; i >= 0; i--) {
    const message = messages[i]
    const attachmentResponseType = message?.messageValue?.attachmentResponseType
    if (
      attachmentResponseType &&
      attachmentResponseType === attachmentTypes.NBA_RESPONSE
    ) {
      nbaObjects = message?.messageValue?.nbaObjects
      break
    }
  }
  if (!nbaObjects) return {}

  nbaObjects?.forEach((buttonTextObj) => {
    const url = buttonTextObj.pageURL
    const label = buttonTextObj.shortText
    const longText = buttonTextObj.longText
    const type = buttonTextObj.type
    const isURL = isValidURL(url)
    const nbaObj = {
      label: label ? label : isURL ? getDomainOutOfURL(url) : url,
      longText: longText,
      url: url,
      type: type
    }
    if (buttonTextObj?.type === constantStrings.ACTIONABLE) {
      actionableNBAOptions.push(nbaObj)
    } else {
      nonActionableNBAOptions.push(nbaObj)
    }
  })
  return { actionableNBAOptions, nonActionableNBAOptions }
}

export function emitEventToParentWindow(currEventType, data, visibility = '') {
  // Patch to never minimise CW for disney bot
  const originalData = data
  if (
    currEventType === eventType.UPDATE_PARENT_WINDOW_STYLE &&
    store.state?.widgetConfig?.brandGPT?.enable &&
    store.state.widgetConfig?.brandGPT?.isResizeOff
  ) {
    new AppLog({
      name: constantStrings.BRAND_GPT,
      message: `${currEventType}: ${JSON.stringify(data)}: `,
      logLevel: 'info',
      service: 'Brand GPT'
    })
    if (data?.width !== '100%' || data?.height !== '100%') {
      return
    }
  }
  if (
    currEventType === eventType.UPDATE_PARENT_WINDOW_STYLE &&
    JSON.stringify(data) !==
      JSON.stringify(chatWidgetScreenSizes.SMALL_SCREEN_DEVICES)
  ) {
    data = setSupportIconPosition(data)
  }
  const isPositionFloat =
    store.getters.knowledgeAiLaunchButtonData?.type === 'float'
  const leftPosition = store.getters.knowledgeAiLaunchButtonData?.position?.x
  const topPosition = store.getters.knowledgeAiLaunchButtonData?.position?.y
  if (
    currEventType === eventType.UPDATE_PARENT_WINDOW_STYLE &&
    JSON.stringify(data) ===
      JSON.stringify(chatWidgetScreenSizes.SMALL_SCREEN_DEVICES) &&
    store.getters.isKnowledgeAiEnabled &&
    Boolean(store.getters.knowledgeAiLaunchButtonData) &&
    isPositionFloat
  ) {
    const regex = /transition\s*:s*[^;]+;/gi
    data.style = data.style.replace(regex, '')
  } else if (
    currEventType === eventType.UPDATE_PARENT_WINDOW_STYLE &&
    store.getters.isKnowledgeAiEnabled &&
    Boolean(store.getters.knowledgeAiLaunchButtonData) &&
    isPositionFloat
  ) {
    const regex = /transition\s*:s*[^;]+;/gi
    data.style = data.style.replace(regex, '')
    if (leftPosition) {
      data.style += `; left: ${leftPosition}`
      data.style += `; right: 0px`
    }
    if (topPosition) {
      data.style += `; top: ${topPosition}`
      data.style += `; bottom: 0px`
    }
  }
  if (currEventType === eventType.UPDATE_PARENT_WINDOW_STYLE) {
    const radius = getComputedStyle(document.documentElement)
      .getPropertyValue('--radius-chat-inner-icon')
      .trim()
    if (visibility === 'standard') {
      data.style += `; box-shadow: 0px 0px 4px rgba(0, 0, 0, 0.1), 0px 0px 12px rgba(0, 0, 0, 0.1); border-radius: ${radius}`
    } else if (visibility === 'x2') {
      data.style += `; box-shadow: 0px 0px 8px rgba(0, 0, 0, 0.2), 0px 0px 24px rgba(0, 0, 0, 0.2); border-radius: ${radius}`
    } else if (visibility === 'x3') {
      data.style += `; box-shadow: 0px 0px 12px rgba(0, 0, 0, 0.3), 0px 0px 36px rgba(0, 0, 0, 0.3); border-radius: ${radius}`
    } else if (visibility === 'default') {
      data.style += `; box-shadow: 0px 0px 4px rgba(0, 0, 0, 0.1), 0px 0px 12px rgba(0, 0, 0, 0.1); border-radius: ${radius}`
    }
  }
  if (
    currEventType === eventType.UPDATE_PARENT_WINDOW_STYLE &&
    JSON.stringify(originalData) ===
      JSON.stringify(chatWidgetScreenSizes.LARGE_SCREEN_DEVICES) &&
    !store.state.isChatWidgetFullScreen
  ) {
    data = setChatWidgetWidthAndHeight(data)
  }
  window.top.postMessage(
    {
      eventType: currEventType,
      ...data
    },
    constantStrings.ASTERISK
  )
}

export function isDateEqual(timestamp1, timestamp2) {
  const date1 = new Date(timestamp1)
  const date2 = new Date(timestamp2)
  if (
    date1.getDate() !== date2.getDate() &&
    date1.getDay() !== date2.getDay()
  ) {
    return true
  } else {
    return false
  }
}

export function getFileExtension(filepath) {
  if (!filepath) return false
  const extension = filepath.split('.')
  return extension[extension.length - 1]?.toLowerCase()
}

export function formatBytes(bytes, decimals = 2) {
  if (bytes === 0) return '0 Bytes'

  const k = 1000
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
}

export function getDateBasedOnTimezone(timeZone) {
  const date = new Date()
  return new Date(date.toLocaleString('en-US', { timeZone }))
}

// pass dates in string(mm-dd-yyyy) format
export function compareDates(firstDate, secondDate) {
  try {
    if (firstDate === secondDate) return 0
    const firstDateConvertArray = firstDate.split('-')
    const secondDateConvertArray = secondDate.split('-')
    firstDate = `${firstDateConvertArray[2]}-${firstDateConvertArray[0]}-${firstDateConvertArray[1]}`
    secondDate = `${secondDateConvertArray[2]}-${secondDateConvertArray[0]}-${secondDateConvertArray[1]}`
    return new Date(firstDate) > new Date(secondDate) ? 1 : -1
  } catch (e) {
    console.log(e)
  }
}

export function isBotRefIdOrWidgetIdChanged(oldWidgetConfig, newWidgetConfig) {
  return (
    oldWidgetConfig.botRefId != newWidgetConfig.botRefId ||
    oldWidgetConfig.widgetId != newWidgetConfig.widgetId
  )
}
export function isWidgetDefaultOpened() {
  return store.state.widgetConfig.isDefaultOpen
}
export function isUpdatedJustNow() {
  let ifUpdated = Storage.getSessionStorageData(
    constantStrings.SESSION_BOT_UPDATED
  )
  if (!ifUpdated) {
    return ifUpdated
  }
  const previous = new Date(
    Storage.getSessionStorageData(constantStrings.SESSION_BOT_UPDATED)
  )
  const now = new Date()
  ifUpdated = now.getTime() - previous.getTime() < 2 * 60 * 1000 ? true : false
  return ifUpdated
}

export function hasConversationInitiated() {
  return (
    getCookie(cookieConstant.SESSION.CONVERSATION_INITIATED)?.toLowerCase() ===
    'true'
  )
}

export function isOauthEnabled() {
  return getCookie(cookieConstant.OAUTH.LOGGED_IN)?.toLowerCase() === 'true'
}

export function hasProActiveGreetingsTriggered() {
  return (
    getCookie(
      cookieConstant.GREETING.PROACTIVE_GREETING_COOKIE_LABEL
    )?.toLowerCase() === 'true'
  )
}

export function hasCalloutBubbleGreetingsTriggered() {
  return (
    getCookie(
      cookieConstant.GREETING.CALLOUT_BUBBLE_GREETING_COOKIE_LABEL
    )?.toLowerCase() === 'true'
  )
}

export function isChatNotStarted() {
  return store.state.chats.length === 0
}

export function getStringFromUUID(length = 0, existingUUID = '') {
  let uuid = existingUUID.length > 0 ? existingUUID : uuidV4()
  return uuid.replaceAll('-', '').substring(0, length)
}

export function addAlphaInHexColor(colorCode, alpha = 0.1) {
  if (colorCode?.length !== 7) return colorCode
  let alphaInHex
  alphaInHex = ((alpha * 255) | (1 << 8)).toString(16).slice(1)
  return colorCode + alphaInHex
}

export function getShade(color, percent) {
  if (color.length === 4) {
    // if hex code is three char hex code. Convert it to 6 char hex code
    color = '#' + color[1].repeat(2) + color[2].repeat(2) + color[3].repeat(2)
  }

  let R = parseInt(color.substring(1, 3), 16)
  let G = parseInt(color.substring(3, 5), 16)
  let B = parseInt(color.substring(5, 7), 16)

  R = parseInt((R * (100 + percent)) / 100, 10)
  G = parseInt((G * (100 + percent)) / 100, 10)
  B = parseInt((B * (100 + percent)) / 100, 10)

  R = R < 255 ? R : 255
  G = G < 255 ? G : 255
  B = B < 255 ? B : 255

  const RR = R.toString(16).length === 1 ? '0' + R.toString(16) : R.toString(16)
  const GG = G.toString(16).length === 1 ? '0' + G.toString(16) : G.toString(16)
  const BB = B.toString(16).length === 1 ? '0' + B.toString(16) : B.toString(16)

  return '#' + RR + GG + BB
}

export function validateHexCode(hex) {
  const pattern = /^#([0-9A-F]{3}){1,2}$/i
  return pattern.test(hex)
}

export function getChatWidgetAlignment() {
  return (
    window.parent.document
      .getElementById('netomiChatWindow')
      ?.getAttribute('position') || 'right'
  )
}

export function captureIoTLogs(log) {
  // By default logs is enabled if key is not present
  const isIoTLogsEnabled = store.state.widgetConfig?.enableIoTLogs
  if (typeof isIoTLogsEnabled === 'undefined' || isIoTLogsEnabled) {
    console.log(log)
  }
}

export function getDeviceType() {
  const userAgent = navigator.userAgent
  const mobileRegex = new RegExp(constantStrings.DEVICE_MOBILE_REGEX)
  const tabletRegex = new RegExp(constantStrings.DEVICE_TABLET_REGEX)

  if (
    tabletRegex.test(userAgent) ||
    (userAgent.toLowerCase().includes('mac') && navigator.maxTouchPoints > 1)
  ) {
    return 'tablet'
  }
  if (mobileRegex.test(userAgent)) {
    return 'mobile'
  }
  return 'desktop'
}

export function getVisitorAttributes() {
  const defaultAttributes = {
    visitor_url: window.parent?.location?.href,
    current_user_agent: navigator?.userAgent,
    current_device_type: getDeviceType(),
    current_platform: getCurrentOperatingSystem(),
    ip_address: store.state.userIPAddress,
    sdk: 'web'
  }
  return defaultAttributes
}

export function getCurrentOperatingSystem() {
  const userAgent = navigator.userAgent
  const systemMap = {
    Win: 'Windows Os',
    Mac: 'MacOs',
    X11: 'UNIX OS',
    Linux: 'LINUX OS'
  }

  for (let os in systemMap) {
    if (userAgent.indexOf(os) != -1) {
      return systemMap[os]
    }
  }
  return 'Unknown'
}

/* ====== AUTHENTICATION UTILS ===== */

export function refreshStorageAndClearChats(clearChats = false) {
  store.dispatch('DELETE_PROACTIVE_GREETING_COOKIE')
  Storage.setSessionStorageData('userInteracted', false)
  setCookie(cookieConstant.SESSION.CREATED_AT, new Date().getTime())
  const cookiesToRemove = [
    { name: cookieConstant.SESSION.CONVERSATION_INITIATED },
    { name: cookieConstant.AUTH.USER_LOGGED_IN },
    { name: cookieConstant.SESSION.CONVERSATION_ID },
    { name: cookieConstant.OAUTH.LOGGED_IN }
  ]
  removeCookies(cookiesToRemove)
  clearChats && store.commit('CLEAR_CHATS')
}

export const isUserLoggedIn = () => {
  return getCookie(cookieConstant.AUTH.USER_LOGGED_IN) === 'true'
}

export const isAuthTokenPresent = () => {
  return Boolean(
    store?.state?.authToken || getCookie(cookieConstant.AUTH.AUTH_TOKEN)
  )
}

// AWS IoT Event Publish Helper
export const IoTPublishEvent = (event_name, data) => {
  mqtt.publishToIoT({
    isEvent: true,
    tabId: Storage.getSessionStorageData('tabId'),
    event_type: event_name,
    payload: data
  })
}

export function getSdkCDN() {
  return GlobalVars.$cdn
}

export function getCdnBaseUrl() {
  const CDN_URL = getSdkCDN() || 'https://chatapps-qa.netomi.com/' // fallback for Local ENV
  const BASE_CDN_URL = new URL(CDN_URL).origin
  return BASE_CDN_URL
}

export function getConstantValue(key) {
  return enConstantStrings[key]
}

export const configureSoundInNotificationService = (sound) => {
  if (sound) {
    notification.add(notificationConstants.BOT_MESSAGE, sound?.bot?.track || '')
    notification.add(
      notificationConstants.USER_MESSAGE,
      sound?.user?.track || ''
    )
    notification.add(
      notificationConstants.TOGGLE,
      `${getCdnBaseUrl()}/assets/sound/default/toggle/toggle.wav`
    )
    const localSoundValue = localStorage.getItem('sound')
    if (store.getters.isSoundEnable) {
      if (!localSoundValue || localSoundValue === 'true') {
        store.dispatch('updateNotificationSound', true)
      } else {
        store.dispatch('updateNotificationSound', false)
      }
    }
  }
}

export function playNotificationSound(state, data) {
  try {
    if ('isUser' in data && state.notificationSound) {
      if (data.isUser) {
        state?.widgetConfig?.sound?.user?.active &&
          notification.play(notificationConstants.USER_MESSAGE)
      } else {
        notification.play(notificationConstants.BOT_MESSAGE)
      }
    }
  } catch (error) {
    console.log('Some error occured in playing sounds', error)
  }
}

export function isEndChatTriggerValidOnConnectionUpdate() {
  const oldTimestamp = Storage.getLocalStorageData(
    constantStrings.CONNECTION_STATUS_LABEL
  )?.lastUpdatedTime
  const newTimestamp = Date.now()
  const delay = newTimestamp - oldTimestamp
  const offlineTimeout = parseInt(
    fetchAgentDeskConfig()?.offlineTimeout ||
      agentDeskProperties.ZENDESK.OFFLINE_TIMEOUT
  )
  return delay >= offlineTimeout
}

export function updateConnectionStatus(event) {
  const updateEvent = new StorageEvent('storage', {
    key: constantStrings.CONNECTION_STATUS_LABEL,
    oldValue: event?.type === status.ONLINE ? status.OFFLINE : status.ONLINE,
    newValue: event?.type
  })
  window.dispatchEvent(updateEvent)

  const previousConnectionStatus = Storage.getLocalStorageData(
    constantStrings.CONNECTION_STATUS_LABEL
  )
  const currentConnectionStatus = {
    status: event?.type,
    lastUpdatedTime: Date.now()
  }
  // event.type gives the current status
  Storage.setLocalStorageData(
    constantStrings.CONNECTION_STATUS_LABEL,
    currentConnectionStatus
  )

  if (
    previousConnectionStatus?.status === status.OFFLINE &&
    currentConnectionStatus.status === status.ONLINE &&
    hasConversationInitiated()
  ) {
    const message = {
      offlineTime: `${new Date(previousConnectionStatus?.lastUpdatedTime)}`,
      offlineTimestamp: previousConnectionStatus?.lastUpdatedTime,
      onlineTime: `${new Date(new Date(currentConnectionStatus.lastUpdatedTime))}`,
      onlineTimestamp: currentConnectionStatus.lastUpdatedTime
    }
    new AppLog({
      name: 'Internet Status',
      message: `User was offline for the time: ${JSON.stringify(message)}`,
      logLevel: 'info',
      service: 'Internet'
    })
  }
}

export function getSdkCDNFromIframe() {
  const CDN = window.parent.document
    .getElementById?.('netomiChatWindow')
    ?.getAttribute('sdk_cdn')
  if (!CDN) {
    return ''
  }
  try {
    return new URL(CDN).origin
  } catch (error) {
    console.warn('Invalid CDN URL from iframe:', CDN, error)
    return ''
  }
}

export function getBotRefIdFromIframe() {
  return window.parent.document
    .getElementById?.('netomiChatWindow')
    ?.getAttribute('botRefId')
}

export function decodeBase64UrlSafe(input) {
  try {
    if (!input || typeof input !== 'string') return input
    const normalized = input.replace(/-/g, '+').replace(/_/g, '/')
    const pad = normalized.length % 4
    const padded = pad ? normalized + '='.repeat(4 - pad) : normalized
    const atobFn =
      typeof atob === 'function'
        ? atob
        : typeof window !== 'undefined' && typeof window.atob === 'function'
          ? window.atob
          : null
    if (atobFn) {
      return atobFn(padded)
    }
    if (typeof Buffer !== 'undefined') {
      return Buffer.from(padded, 'base64').toString('utf-8')
    }
    return input
  } catch {
    return input
  }
}

export async function lazyLoadLiveAgent() {
  let LiveAgent = await import(
    /* webpackChunkName: "liveagent_sdk" */ '@/services/liveAgent'
  )
  new AppLog({
    name: 'Live Agent SDK',
    message: 'Live Agentdesk SDK has been imported',
    logLevel: 'info',
    service: 'LIVEAGENT'
  })
  GlobalVars.$liveAgent = new LiveAgent.default()
}

export function getConnectionStatus() {
  return store?.state?.connectionStatus
}

export function cancelHTTPRequests() {
  // Add more conditions if needed here for http cancelation
  return !getConnectionStatus()
}

export function hasProActiveGreetingsConfigured() {
  return Boolean(store.state?.widgetConfig?.proActiveGreetings)
}

export function showCWBasedOnScreenSize() {
  store.dispatch('toggleChatWindowStatus', !store.state.isWindowMaximized)
  if (
    store.state.widgetConfig?.isDefaultFullScreen &&
    window.outerWidth >= 768
  ) {
    emitEventToParentWindow(
      eventType.UPDATE_PARENT_WINDOW_STYLE,
      chatWidgetScreenSizes.SMALL_SCREEN_DEVICES
    )
  } else if (window.outerWidth >= 768) {
    emitEventToParentWindow(
      eventType.UPDATE_PARENT_WINDOW_STYLE,
      chatWidgetScreenSizes.LARGE_SCREEN_DEVICES
    )
  }
}

export async function getFilewithoutEXIFdata(res) {
  let file = res
  await new Promise((resolve) => {
    const img = new Image()
    img.src = URL.createObjectURL(file)
    img.onload = function () {
      const canvas = document.createElement('canvas')
      canvas.width = img.width
      canvas.height = img.height
      const ctx = canvas.getContext('2d')
      ctx.drawImage(img, 0, 0)
      canvas.toBlob(
        function (blob) {
          const br = new Blob([blob], { type: file.type })
          const data = new FormData()
          data.append('file', br, file.name)
          file = data.get('file')
          resolve()
        },
        file.type,
        0.5
      )
    }
  })
  return file
}

export const isWindowMaximized = () => {
  return store.state.isWindowMaximized
}

export const isIOS = () => {
  return (
    [
      'iPad Simulator',
      'iPhone Simulator',
      'iPod Simulator',
      'iPad',
      'iPhone',
      'iPod'
    ].includes(navigator.platform) ||
    // iPad on iOS 13 detection
    (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
  )
}

export const isMacSafari = () => {
  return (
    navigator.userAgent.includes('Mac') &&
    navigator.userAgent.toLowerCase().indexOf('safari/') > -1
  )
}

export function clearAllSetTimeout(timers) {
  timers.forEach((t) => clearTimeout(t))
}

export function blankScreenCheck(cause) {
  // Check for blank screen after few seconds
  const time =
    store.state.widgetConfig?.greetingsResponseWaitTimeBeforeLogging || 60000
  const timerId = setTimeout(() => {
    isBlankScreen(cause || constantStrings.EMPTY_PROACTIVE_RESPONSE)
  }, time)
  store.dispatch('updateBlankScreenState', { timerId })
}

export const isBlankScreen = (cause) => {
  const chatsArray = store.state.chats
  // If chats array length is 0 OR the first chats array element has no message property or message field is empty then it is a blank screen
  // We need to check the first chats array element in case of greetings not getting response
  const chatsArrayLenCheck = !chatsArray.length
  const greetingsCheck =
    chatsArray.length === 1 &&
    (!chatsArray[0]?.messages || !chatsArray[0]?.messages.length)
  const pillCheck = chatsArray.length === 1 && chatsArray[0]?.isMessageSeparator
  if (chatsArrayLenCheck || greetingsCheck || pillCheck) {
    new AppError({
      name: 'Blank Screen Detected',
      message: `Blank screen caused due to ${cause}`
    })
  }
}

export const getTranslation = (constant) => {
  try {
    // Use the t method we added to the I18n class
    return I18n.t(constant)
  } catch (error) {
    console.error('Translation error:', error)
    return constant // fallback to the key itself
  }
}

export const getMinTimeForTypingIndicator = () => {
  if (isTypeBOT() && store.getters.isProgressiveTypingIndicatorEnabled) {
    return (
      store.state.widgetConfig.progressiveTypingIndicator?.minTime ||
      constantStrings.DEFAULT_MIN_TIME_FOR_TYPING_INDICATOR
    )
  }
  return (
    store.state.widgetConfig.typingIndicator?.minTime ||
    constantStrings.DEFAULT_MIN_TIME_FOR_TYPING_INDICATOR
  )
}

export const getMaxTimeForTypingIndicator = () => {
  if (isTypeBOT() && store.getters.isProgressiveTypingIndicatorEnabled) {
    return (
      store.getters.getProgressiveTypingIndicatorConfig?.reduce(
        (maxTime, config) => maxTime + parseInt(config.duration),
        0
      ) || constantStrings.DEFAULT_MAX_TIME_FOR_TYPING_INDICATOR
    )
  }
  return (
    store.state.widgetConfig.typingIndicator?.maxTime ||
    constantStrings.DEFAULT_MAX_TIME_FOR_TYPING_INDICATOR
  )
}

export const checkErrorFromSocial = (errorResponse) => {
  if (
    ![apiErrors.SUCCESS, apiErrors.REQUEST_ACCEPTED].includes(
      errorResponse?.data?.statusCode
    )
  ) {
    const customError = new Error()
    customError.statusCode =
      errorResponse?.data?.statusCode || apiErrors.DEFAULT.STATUS_CODE
    customError.statusMessage =
      errorResponse?.data?.statusMessage || apiErrors.DEFAULT.STATUS_MSG
    customError.status = errorResponse?.status || apiErrors.DEFAULT.STATUS
    customError.exceptionCode =
      errorResponse?.data?.exceptionCode || apiErrors.DEFAULT.EXCEPTION_CODE
    return customError
  }
}

export const addCustomErrors = (currError) => {
  if (currError?.statusCode) {
    return {
      statusCode: currError?.statusCode,
      statusMessage: currError?.statusMessage,
      status: currError?.status,
      custom: currError?.customMessage,
      exceptionCode: currError?.exceptionCode
    }
  }
}

export const isDismissCalloutBubbleOnCrossEnabled = () => {
  const dismissCalloutBubbleOnCross = constantStrings.DISMISS_CB_ON_CROSS
  if (
    getCookie(cookieConstant.SESSION.CONVERSATION_ID) !==
    Storage.getLocalStorageData(dismissCalloutBubbleOnCross)
  )
    Storage.removeLocalStorageData(dismissCalloutBubbleOnCross)
  return (
    !!Storage.getLocalStorageData(dismissCalloutBubbleOnCross) &&
    store.state?.widgetConfig?.calloutBubble?.dismissCalloutBubbleOnCross &&
    Storage.getLocalStorageData(dismissCalloutBubbleOnCross) ===
      getCookie(cookieConstant.SESSION.CONVERSATION_ID)
  )
}

export const checkForEmptyGreetingsResponse = (data) => {
  const labelType = data?.requestPayload?.messagePayload?.label
  if (labelType === constantStrings.PROACTIVE_GREETING_LABEL) {
    new AppWarn({
      name: 'Empty Greetings',
      message: `Proactive greetings response is empty`
    })
  } else if (labelType === constantStrings.CALLOUT_BUBBLE_TEXT) {
    new AppWarn({
      name: 'Empty Greetings',
      message: `Callout bubble greetings response is empty`
    })
  }
}

// cw-accessibility
export const setNewUnreadMessagesText = (message) => {
  let ariaLabelText =
    !message || !message.attachments || message?.attachments?.length <= 1
      ? ''
      : `${message.attachments.length}, new unread messages`
  document
    .getElementById('unread-messages-text')
    ?.setAttribute('aria-label', ariaLabelText)
}

export const setFocusOnFirstInterativeNode = () => {
  store.dispatch('updateFocusEnable', false)
  const lastChatBlock = store.state.chats[store.state.chats.length - 1]
  const newMessageIds = []
  lastChatBlock?.messages?.forEach((message) => {
    newMessageIds.push(message.messageId)
    if (message?.messageValue?.isReviewEnabled)
      newMessageIds.push(`feedback-${message.messageId}`)
  })

  // add quick reply node
  newMessageIds.push('quick-reply-wrapper')

  for (let messageId of newMessageIds) {
    const currentMessageNode = document.getElementById(messageId)
    const node = currentMessageNode?.querySelector(
      'a, button, input, textarea, select, [tabindex]:not([tabindex="-1"])'
    )
    if (node) {
      document.removeEventListener('keydown', setFocusElement)
      store.dispatch('updateFocusNode', node)
      store.dispatch('updateFocusEnable', true)
      document.addEventListener('keydown', setFocusElement)
      break
    }
  }
}

function setFocusElement(event) {
  const focusEnable = store.state.focusEnable
  if (event.key === 'Tab' && focusEnable) {
    event.preventDefault()
    const focusNode = store.state.focusNode
    focusNode?.focus()
    document.removeEventListener('keydown', setFocusElement)
    store.dispatch('updateFocusEnable', false)
  }
}

export const focusOnChatInputField = (fallbackFocus = false) => {
  if (
    store.state.widgetConfig?.footerStatus !== constantStrings.DISABLED &&
    !store.state.inputFieldDisableStatus &&
    !store.state.inputFieldTempDisableStatus
  ) {
    document.getElementById('text-area')?.focus()
    store.dispatch('updateFocusNode', null)
    store.dispatch('updateFocusEnable', false)
  } else {
    fallbackFocus && setFocusOnFirstInterativeNode()
  }
}

export const updateCalloutBubble = () => {
  let calloutBubble = store.state.widgetConfig?.calloutBubble
  if (!calloutBubble) return
  const clientURL = window.parent.location.href
  const clientURLWithoutProtocol = clientURL.replace(/^http(s?):\/\//i, '')
  calloutBubble = Array.isArray(calloutBubble) ? calloutBubble : [calloutBubble]
  const calloutBubbleObject = calloutBubble.find((cbObject, index) => {
    const url = cbObject?.url || constantStrings.ASTERISK
    if (
      isURLMatch(clientURL, url) ||
      isURLMatch(clientURLWithoutProtocol, url)
    ) {
      cbObject.index = index + 1
      return cbObject
    }
  })
  store.dispatch('updateCalloutBubble', calloutBubbleObject)
}

/**
 * checks whether client url matches with given url in bot json
 * url in bot json may include '*'(Matches any sequence of characters including the empty sequence) or '?'(Matches any single character)
 * @param {string} clientURL
 * @param {string} url
 * @example
 * isURLMatch('netomi.com/sales/team1', 'netomi.com/sales/*') => true
 * isURLMatch('netomi.com/sales', 'netomi.com/support') => false
 * isURLMatch('netomi.com/sales', '*.com/sales') => true
 * @return {boolean}
 */
const isURLMatch = function (clientURL, url) {
  let clientURLIndex = 0,
    urlIndex = 0
  let saveURLIndex = -1,
    saveClientURLIndex = -1

  while (clientURLIndex < clientURL.length) {
    if (
      (urlIndex < url.length && clientURL[clientURLIndex] === url[urlIndex]) ||
      url[urlIndex] === '?'
    ) {
      clientURLIndex++
      urlIndex++
    } else if (
      urlIndex < url.length &&
      url[urlIndex] === constantStrings.ASTERISK
    ) {
      saveURLIndex = urlIndex
      saveClientURLIndex = clientURLIndex
      urlIndex++
    } else if (saveURLIndex === -1) return false
    else {
      urlIndex = saveURLIndex + 1
      clientURLIndex = saveClientURLIndex + 1
      saveClientURLIndex = clientURLIndex
    }
  }
  for (let idx = urlIndex; idx < url.length; idx++) {
    if (url[idx] !== constantStrings.ASTERISK) return false
  }
  return true
}

export function changeCWLanguage(languageCode) {
  const multilingual = store.state.widgetConfig?.multilingual
  const isAutoLocalisationEnabled = multilingual?.isAutoLocalisationEnabled
  if (!multilingual || !isAutoLocalisationEnabled) return
  const languages = multilingual.languages
  if (languages.find(({ code }) => code === languageCode)) {
    OptionItemJsonConfig.LANGUAGE_CONFIG.clickHandler({ code: languageCode })
  }
}

export function handleScrollToLastUserMessage({
  scrollToLastMessage = false
} = {}) {
  const element = document.getElementById('chat-list-wrapper')
  if (!element) return
  setTimeout(() => {
    const lastUserMessageId = getLastUserMessageId(store.state.chats)
    const stopScrollDiv = document.getElementById(lastUserMessageId)
    if (!stopScrollDiv || scrollToLastMessage) {
      const chatsArr = store.state.chats
      const lastAddedChat = chatsArr[(chatsArr || []).length - 1]

      const lastMessagesAccumulatedHeight = (
        lastAddedChat?.messages || []
      ).reduce((acc, message) => {
        const messageID = message?.messageId
        const messageElement = document.getElementById(messageID)
        const messageContHeight = messageElement?.offsetHeight || 0

        let quickReplyContHeight = 0
        if (message?.messageValue?.quickReply?.options?.length) {
          const quickReplyContainer = document.getElementById(
            'quick-reply-wrapper'
          )
          if (quickReplyContainer) {
            quickReplyContHeight = quickReplyContainer?.offsetHeight || 0
          }
        }
        acc += messageContHeight + quickReplyContHeight
        return acc
      }, 0)
      const chatWrapperHeight = element?.offsetHeight || 0
      if (lastMessagesAccumulatedHeight > chatWrapperHeight) {
        const lastChatMessageID = lastAddedChat?.messages?.[0]?.messageId
        let lastChatElement = document.getElementById(lastChatMessageID)
        if (!lastChatElement) {
          for (const message of lastAddedChat?.messages || []) {
            const element = document.getElementById(message?.messageId)
            if (element) {
              lastChatElement = element
              break
            }
          }
        }
        if (lastChatElement) {
          const lastChatOffsetTop = lastChatElement?.offsetTop || 0
          element.scrollTop = lastChatOffsetTop - 8
        }
      } else {
        element.scrollTop = element.scrollHeight
      }
    } else {
      let distanceToTop = 86
      distanceToTop = stopScrollDiv?.getBoundingClientRect()?.top
      if (distanceToTop > 85) {
        element.scrollTop = stopScrollDiv.offsetTop - 6
      }
    }
  }, 100)
}

export function getLastUserMessageId(chats) {
  for (let i = chats.length - 1; i >= 0; i--) {
    if (chats[i]?.isUser) {
      return chats[i]?.messages?.[0]?.messageId
    }
  }
  return ''
}

export function setSupportIconPosition(data) {
  const launchButton = store.state.widgetConfig?.launchButton
  if (!launchButton) return data
  const position = launchButton?.position || []
  const defaultPosition = launchButton?.defaultPosition
  let deviceType = getDeviceType()
  if (deviceType === 'desktop') {
    deviceType =
      window.parent.outerWidth > 1920 ? 'largeDesktop' : 'standardDesktop'
  }
  if (Array.isArray(position)) {
    const {
      left: leftPosition,
      right: rightPosition,
      top: topPosition,
      bottom: bottomPosition
    } = position.find(
      ({ devices }) => devices.includes(deviceType) || devices.includes('all')
    ) || { bottom: '10px', [defaultPosition]: '10px' }
    const regex = /transition\s*:s*[^;]+;/gi
    data.style = data.style.replace(regex, '')
    if (leftPosition) {
      data.style += `; left: ${leftPosition}`
    } else if (rightPosition) {
      data.style += `; right: ${rightPosition}`
    }
    if (topPosition) {
      data.style += `; top: ${topPosition}`
    } else if (bottomPosition) {
      data.style += `; bottom: ${bottomPosition}`
    }
  }
  return data
}

export function removeConversationBeforeRestart(chatHistory) {
  const historyArr = chatHistory?.data?.responses || []

  for (let i = historyArr.length - 1; i >= 0; i--) {
    if (
      historyArr[i]?.eventObject?.eventData?.subType ===
      constantStrings.CHAT_RESTART
    ) {
      // eslint-disable-next-line no-extra-semi
      ;(chatHistory?.data?.responses || []).splice(0, i)
      break
    }
  }
}

export function getPreviousTimestamp(
  chatHistory,
  numberOfMessages,
  previousTimestamp
) {
  const historyArr = chatHistory?.data?.responses || []
  const previousConversationId = chatHistory?.data?.previousConversationId

  // If there's no previous conversation and messages are less than required
  if (!previousConversationId && historyArr.length < numberOfMessages) {
    return null
  }

  // If previousTimestamp is truthy, return last message timestamp, else first
  return previousTimestamp
    ? historyArr.at(-1)?.timestamp
    : historyArr?.[0]?.timestamp
}

export function isfullHistoryLoaded(chatHistory, minmumMessages) {
  const historyArr = chatHistory?.data?.responses
  return historyArr.length < minmumMessages
}

export async function uploadOnS3(file) {
  try {
    const requestBody = {
      file,
      uploadKeyPrefix: file.name
    }
    const key = await store.dispatch('uploadOnS3', requestBody)
    if (!key) {
      throw new Error(`Error received while running uploadOnS3, Key: ${key}`)
    }
    return key
  } catch (error) {
    throw new Error(error.message)
  }
}

export function getAttachmentTypeFromExtension(fileName) {
  const extension = getFileExtension(fileName)
  if (isImage(extension)) {
    return 'image'
  } else if (isVideo(extension)) {
    return 'video'
  } else if (isSound(extension)) {
    return 'sound'
  } else {
    return 'file'
  }
}

export function sanitizeAttachmentTypes(attachmentTypes) {
  return attachmentTypes.map((type) => `.${type.toLowerCase()}`)
}

export function isVideoSupportedFormat(extn) {
  return ['mp4', 'webm', 'ogg', 'mov'].includes(extn)
}

export function isImage(extn) {
  return ['png', 'jpeg', 'jpg', 'gif', 'svg'].includes(extn)
}

export function isVideo(extn) {
  return [
    'mp4',
    'mov',
    'avi',
    'wmv',
    'flv',
    'mp2',
    'mpeg',
    'm4p',
    'avchd',
    '3gp',
    'mkv'
  ].includes(extn)
}

export function isSound(extn) {
  return [
    'mp3',
    'wav',
    'aac',
    'flac',
    'm4a',
    'wma',
    'aiff',
    'alac',
    'amr'
  ].includes(extn)
}

export function isFileValid(
  extn,
  actualType,
  allowedFileFormatList = store.state.widgetConfig.fileSharing.list
) {
  if (actualType === attachmentTypes.Video && !isVideoSupportedFormat(extn)) {
    store.dispatch('updateFileAttachmentIcon', false)
    throw new Error('Invalid video type')
  } else if (actualType === attachmentTypes.Image && !isImage(extn)) {
    store.dispatch('updateFileAttachmentIcon', false)
    throw new Error('Invalid image type')
  } else {
    if (!allowedFileFormatList.includes(`.${extn}`)) {
      store.dispatch('updateFileAttachmentIcon', false)
      throw new Error('Invalid file type')
    }
  }

  return true
}

export function isFileSizeValid(
  size,
  maxSize = store.state.widgetConfig.fileSharing.fileSize
) {
  if (!size) {
    throw new Error('File cannot be empty')
  } else if (size > maxSize) {
    store.dispatch('updateFileAttachmentIcon', false)
    throw new Error(
      `File size limit: ${formatBytes(store.state.widgetConfig.fileSharing.fileSize)}.`
    )
  }
}

export async function uploadFile(file) {
  file = new File([file], file.name.replaceAll(/\s/g, ''))
  let extn = getFileExtension(file.name)

  if (
    ['png', 'jpg', 'jpeg'].includes(extn) &&
    store.state?.widgetConfig?.removeExifData
  ) {
    file = await getFilewithoutEXIFdata(file)
  }

  if (file) {
    store.dispatch('updateFileAttachmentIcon', true)
    const fileSize = file.size
    const fileName = file.name && file.name.split('.')[0]
    const attachment = {
      type: attachmentTypes.File,
      actualType: isImage(extn)
        ? attachmentTypes.Image
        : isVideo(extn)
          ? attachmentTypes.Video
          : attachmentTypes.File,
      attachmentId: uuidV4(),
      percentage: 10,
      fileType: extn,
      title: `${fileName}.${extn}`,
      fileSize: file.size
    }
    try {
      addToChat({
        attachments: [
          {
            ...attachment,
            errorMessage: null
          }
        ],
        isAppStateOnly: true
      })
      isFileSizeValid(fileSize)
      isFileValid(extn, attachment.actualType)
      const fileURL = await uploadOnS3(file)
      addToChat({
        userMessage: {
          payload: constantStrings.MEDIA_UPLOADED,
          label: store.state.widgetConfig.fileSharing.attachmentQuery
        },
        attachments: [
          {
            ...attachment,
            type: isImage(extn)
              ? attachmentTypes.Image
              : isVideo(extn)
                ? attachmentTypes.Video
                : attachmentTypes.File,
            [isImage(extn)
              ? 'largeImageUrl'
              : isVideo(extn)
                ? 'thumbnailUrl'
                : 'fileURL']: fileURL,
            errorMessage: null
          }
        ],
        updateLastChatBlock: true
      })
      const attachment_type = isImage(extn)
        ? attachmentTypes.Image
        : isVideo(extn)
          ? attachmentTypes.Video
          : attachmentTypes.File
      store.state.widgetConfig?.eventAnalyticsMP &&
        GlobalVars.$segment.global.sendEvent('attachment_upload_successful', {
          attachmentType: attachment_type
        })
    } catch (err) {
      console.log('Error While Uploading', err.message)
      store.dispatch('updateTypingIndicatorStatus', false)
      addToChat({
        attachments: [
          {
            ...attachment,
            errorMessage: err.message
          }
        ],
        isAppStateOnly: true,
        updateLastChatBlock: true,
        hideTypingIndicator: true
      })
      throw new Error(`Error while Uploading media. ${err.message}`)
    } finally {
      store.dispatch('updateFileAttachmentIcon', false)
    }
  }
}

function addToChat(data) {
  store.dispatch('sendMessage', data)
}

export function setChatWidgetWidthAndHeight(data) {
  const chatWidgetWidth = store.getters.dynamicChatWidgetWidth
  const chatWidgetHeight = store.getters.dynamicChatWidgetHeight
  if (chatWidgetWidth) {
    const width = validateAndGetDataInPx(chatWidgetWidth)
    if (width && width >= 360 && width <= 420) {
      data.style += `; width: ${width}px`
    }
  }
  if (chatWidgetHeight) {
    const height = validateAndGetDataInPercentage(chatWidgetHeight)
    if (height && height >= 50 && height <= 90) {
      data.style += `; height: ${height}%`
      data.style = data.style.replaceAll('max-height', '')
    }
  }

  return data
}

export function validateAndGetDataInPercentage(data) {
  const percentPattern = /^(\d+(\.\d+)?)%$/
  const match = data.match(percentPattern)
  if (match) {
    return parseFloat(match?.[1])
  }
}

export function validateAndGetDataInPx(data) {
  const pxPattern = /^(\d+(\.\d+)?)px$/
  const match = data.match(pxPattern)
  if (match) {
    return parseFloat(match?.[1])
  }
}

export function isResponseBackendStreaming(data) {
  if (data.customPayload) {
    const customPayload = data.customPayload
    const chunkIndex = customPayload?.CHUNK_INDEX
    const status = customPayload?.CHUNK_STATUS
    const isLastMessageExtension = customPayload.isLastMessageExtension || false
    if (chunkIndex && status) {
      const chunkIndexValue = parseInt(chunkIndex)
      if (
        (chunkIndexValue === 0 && status === 'SUCCESS') ||
        isLastMessageExtension
      ) {
        return false
      }
      return true
    }
  }
  return false
}

export function getDataInChunks(data, chunkLength = 8) {
  const val = data?.replace(/\n/g, '<br/>')
  const arr = []
  const pattern = /\s+(?=[^>]*(?:<|$))/g
  let words = val?.split(pattern)
  for (let i = 0; i < words.length; i += chunkLength) {
    const str = words.slice(i, i + chunkLength).join(' ')
    const isWithinLimit = i + chunkLength + 1 <= words.length
    if (isWithinLimit) {
      arr.push(str + ' ')
    } else {
      arr.push(str)
    }
  }
  return arr
}

export function addFileNameInStorage(fileName, fileURL) {
  const uuid = fileURL.substring(fileURL.lastIndexOf('/') + 1)
  const currentStorageValue =
    Storage.getLocalStorageData(constantStrings.FILE_NAME_UUID) || {}
  Storage.setLocalStorageData(constantStrings.FILE_NAME_UUID, {
    ...currentStorageValue,
    [uuid]: fileName
  })
}

export const getThemeDefaultColor = (colorKey = '--widget-primary-color') => {
  const root = document.documentElement // Get the computed styles of the :root elementconst
  const computedStyle = getComputedStyle(root)

  return computedStyle.getPropertyValue(colorKey).trim()
}

export async function handleMissedMessages() {
  if (
    store.state.widgetConfig?.liveAgentDeskConfig?.agentDeskChannelName ===
      constantStrings.ZENDESK &&
    !isTypeBOT()
  )
    return
  const chats = store.state.chats
  const lastRequestIdFromRedis = await getLastRequestId()
  let lastRequestId = null
  for (let index = chats.length - 1; index >= 0; index--) {
    if (chats[index].requestId) {
      lastRequestId = chats[index].requestId
      break
    }
  }
  if (
    lastRequestId &&
    lastRequestIdFromRedis &&
    lastRequestId !== lastRequestIdFromRedis
  ) {
    const message = {
      requestIdInCW: lastRequestId,
      requestIdInSocialRedis: lastRequestIdFromRedis,
      ownerType: store.state.controlType
    }
    new AppWarn({
      name: 'Missed Message',
      message: `Missed message case is detected: ${JSON.stringify(message)}`
    })
    if (!isTypeBOT()) {
      new AppLog({
        name: 'Missed Message Webhook History v2 API',
        message: 'Fetching Webhook History for missed message case'
      })
      const requestIds = await store.dispatch('updateMissedMessagesFromHistory')
      if (requestIds?.length) {
        new AppLog({
          name: 'Missed Message Webhook History v2 API',
          message: `Total Cases of Missed Chats found: ${requestIds.length}, Missing messages requestIds: ${requestIds}`
        })
      }
    }
  }
}

export function checkIfRequestIdExists(requestId) {
  const chats = store.state.chats
  for (let index = chats.length - 1; index >= 0; index--) {
    if (requestId === chats[index].requestId) {
      return true
    }
  }
  return false
}

export function getEventDataPoints() {
  return {
    botRefId: Storage.getLocalStorageData('widgetConfig')?.botRefId || null,
    pageUrl: window.parent?.location?.href || '',
    conversationId: getCookie(cookieConstant.SESSION.CONVERSATION_ID) || null,
    event: {
      timeStamp: Date.now()
    },
    shouldPublishEvent: true
  }
}

export function convertUTCTimestampToLocalTimestamp(UTCTimestamp) {
  const localOffsetInMs = new Date().getTimezoneOffset() * 60 * 1000
  return UTCTimestamp - localOffsetInMs
}

export function getScheduledAlert(alerts) {
  for (let index = 0; index < alerts.length; index++) {
    const currentAlert = alerts[index]
    if (currentAlert.isArchived || !currentAlert.schedule.isEnabled) continue
    const currentTimestamp = Date.now()
    const startTimestamp = convertUTCTimestampToLocalTimestamp(
      currentAlert.schedule.startEndDateTime[0] -
        currentAlert.schedule.utcOffsetInMs
    )
    const endTimestamp = convertUTCTimestampToLocalTimestamp(
      currentAlert.schedule.startEndDateTime[1] -
        currentAlert.schedule.utcOffsetInMs
    )
    if (currentTimestamp >= startTimestamp && currentTimestamp < endTimestamp) {
      return currentAlert
    }
  }
  return
}

export function getUpcomingScheduledAlert(alerts) {
  let delay = null
  let upcomingScheduledHighPriorityAlert = null
  for (let index = 0; index < alerts.length; index++) {
    const currentAlert = alerts[index]
    if (currentAlert.isArchived || !currentAlert.schedule.isEnabled) continue
    const currentTimestamp = Date.now()
    const startTimestamp = convertUTCTimestampToLocalTimestamp(
      currentAlert.schedule.startEndDateTime[0] -
        currentAlert.schedule.utcOffsetInMs
    )
    const currentAlertDelay = startTimestamp - currentTimestamp
    if (currentAlertDelay > 0) {
      if (delay === null || delay > currentAlertDelay) {
        delay = currentAlertDelay
        upcomingScheduledHighPriorityAlert = currentAlert
      }
    }
  }
  return { upcomingScheduledHighPriorityAlert, delay }
}

export function isMediumPriorityAlertValid() {
  return (
    !hasConversationInitiated() &&
    !Object.keys(store.state.alerts.highPriorityAlert)?.length
  )
}

export function isMultiSourceLastExtension(data, messages) {
  const isLastMessageExtension =
    data?.customPayload?.isLastMessageExtension || false
  return (
    isLastMessageExtension &&
    messages?.length === 1 &&
    messages?.[0]?.type === 'MultiSource'
  )
}

export function removeCalloutBubbleContainer() {
  WindowHelper.getWindow('parent')
    ?.document?.getElementById('callout-bubble-container')
    ?.remove()
}

export function getVoiceIdFromLanguage() {
  const languageVoiceMap = store.getters.languageVoiceMap
  const currentLocale = store.state.lastMessageLocale
  let voice_id = languageVoiceMap?.[currentLocale]?.voice_id
  if (!voice_id && currentLocale === 'en') {
    voice_id = store.getters.elevenLabsConnectionConfigs?.voice_id
  }
  return voice_id
}

export function getVoiceNameAndLocaleFromLanguage() {
  const languageVoiceMap = store.getters.languageVoiceMap
  const currentLocale = store.state.lastMessageLocale
  let langCode = languageVoiceMap?.[currentLocale]?.langCode
  let voiceName = languageVoiceMap?.[currentLocale]?.voiceName

  if ((!langCode || !voiceName) && currentLocale === 'en') {
    voiceName = store.state.widgetConfig?.voiceConfig?.voiceName
    langCode = store.state.widgetConfig?.voiceConfig?.langCode
  }

  return {
    langCode,
    voiceName
  }
}

export function getElevenLabsConnectionConfigs() {
  const elevenLabsConnectionConfigs = store.getters.elevenLabsConnectionConfigs
  const voice_id = getVoiceIdFromLanguage()
  const model_id = 'eleven_flash_v2_5'
  return {
    voice_id: voice_id,
    model_id: elevenLabsConnectionConfigs?.model_id || model_id,
    stability: elevenLabsConnectionConfigs?.stability || 0.5,
    speed: elevenLabsConnectionConfigs?.speed || 1,
    similarity_boost: elevenLabsConnectionConfigs?.similarity_boost || 0.75,
    style: elevenLabsConnectionConfigs?.style || 0,
    apply_text_normalization:
      elevenLabsConnectionConfigs?.apply_text_normalization || 'on'
  }
}

export function renderCalloutBubble() {
  if (
    !isDismissCalloutBubbleOnCrossEnabled() &&
    store.state.widgetConfig?.calloutBubble?.calloutBubbleEnabled &&
    !store.getters.isKnowledgeAiEnabled
  ) {
    const fontFamily = customFont.getCurrentFontFamily()
    const fontSize =
      fontSizes?.['bodyText']?.[
        store.getters.isFontSizeChatInterfaceOverrideEnabled
          ? store.getters.getFontSizeType
          : 'default'
      ] || '14px'
    // Pass theme values to avoid cross-origin access in callout-bubble iframe
    const primaryColor = getThemeDefaultColor('--widget-primary-color')
    const borderRadius = getThemeDefaultColor('--radius-chat-outer-curve')
    const contextConfig = store.state.calloutBubblePubsubConfig?.contextConfig
    if (contextConfig?.length) {
      const language = store.state.calloutBubblePubsubConfig?.language || 'en'
      const clientUrl = window.parent.location.href
      const clientURLWithoutProtocol = clientUrl.replace(/^http(s?):\/\//i, '')
      const matchedConfig = contextConfig.find((cfg) => {
        const url = cfg.url
        return (
          cfg.language === language &&
          (isURLMatch(clientUrl, url) ||
            isURLMatch(clientURLWithoutProtocol, url))
        )
      })

      if (matchedConfig) {
        const messages = matchedConfig?.messages || []
        const quickReplies = matchedConfig?.quickReplies || []
        if (messages.length || quickReplies.length) {
          const attachments = messages.map((msg, index) => {
            const attachment = {
              type: 'Text',
              text: msg
            }
            if (index === messages.length - 1 && quickReplies.length) {
              attachment.quickReply = {
                options: quickReplies
              }
            }
            return attachment
          })
          if (attachments) {
            if (
              store.state.widgetConfig &&
              store.state.widgetConfig.calloutBubble
            ) {
              store.state.widgetConfig.calloutBubble.attachments = attachments
            }
          }
        }
      }
    }
    calloutBubbleMessanger.sendMessage({
      eventType: 'RENDER_CALLOUT_OPTIONS',
      data: {
        ...(store.state.widgetConfig?.calloutBubble || {}),
        ...(store.state.widgetConfig?.launchButton || {}),
        proActiveGreetings: store.state.widgetConfig?.proActiveGreetings || {},
        calloutBubbleProperties: store.state.calloutBubblePubsubConfig || {},
        properties: {
          fontFamily,
          fontSize,
          primaryColor,
          borderRadius
        }
      }
    })
  }
}

export function updateIframeHeaderTitle(title) {
  const iframe = window.parent.document.getElementById('netomiChatWindow')
  if (!iframe) return

  const iframeDocument = iframe.contentDocument || iframe.contentWindow.document
  if (!iframeDocument) return

  const titleElement = iframeDocument.querySelector('title')
  if (titleElement) {
    titleElement.innerHTML = title
  }
}

export function formatTimestampToMDYDate(msTimestamp) {
  if (typeof msTimestamp !== 'number') {
    throw new Error('Timestamp must be a number (in milliseconds)')
  }

  const date = new Date(msTimestamp)
  if (isNaN(date.getTime())) {
    throw new Error('Invalid timestamp')
  }

  const month = String(date.getUTCMonth() + 1).padStart(2, '0')
  const day = String(date.getUTCDate()).padStart(2, '0')
  const year = date.getUTCFullYear()

  return `${month}-${day}-${year}`
}
